import { useEffect, useRef, useState } from "react";

import { mumHubAudienceToken } from "@cch/shared/mum-hub-audience";
import { decideMumHubAccess } from "@cch/shared/mum-hub-remote-config";

import { useAuth } from "@/hooks/use-auth";

import { mumHubRemoteConfigOnce } from "./remote-config";

/**
 * The Firebase arm of the Mum Hub gate, as a hook (MAR-235).
 *
 * Mirrors the app's `useMumHubIdentitySync` in `mobile/src/lib/feature-flags/
 * mum-hub-flag.ts`, minus the pieces the web does not need. The app has to
 * defend against an account switch landing one customer's decision under
 * another on a shared phone, which is why it carries a generation counter and
 * an identity-scoped store. A browser tab is not a shared phone, but the same
 * hazard exists in miniature — sign out, sign in as someone else, and a token
 * computed for the first email must not settle under the second — so the
 * effect is keyed on the customer id and a superseded run is discarded.
 *
 * ## The three states, and why "pending" is one of them
 *
 * Answering "not allowed" while the fetch is in flight renders the not-found
 * page and then replaces it, which is a flash of 404 on a page the customer
 * was told she could use. So this reports `pending` until it knows, exactly
 * as the entitlements arm does, and the gate draws nothing gate-dependent
 * meanwhile.
 *
 * Signed out there is nothing to wait for and no token to compute, so it
 * settles `idle` immediately without touching Firebase. The global switch is
 * deliberately not consulted for a signed-out visitor: the app's
 * `decideMumHubVisible` requires a signed-in customer before anything else,
 * and the website's static build flag is what opens the feature to the
 * public.
 */

export type MumHubFirebaseStatus = "idle" | "pending" | "settled";

export interface MumHubFirebaseAccess {
  status: MumHubFirebaseStatus;
  /** Only ever true once `status` is "settled". */
  allowed: boolean;
}

const IDLE: MumHubFirebaseAccess = { status: "idle", allowed: false };
const PENDING: MumHubFirebaseAccess = { status: "pending", allowed: false };

export function useMumHubFirebaseAccess(): MumHubFirebaseAccess {
  const { customer, isLoading } = useAuth();
  const customerId = customer?.id ?? null;
  const email = customer?.email ?? null;

  const [access, setAccess] = useState<MumHubFirebaseAccess>(IDLE);
  // Bumped on every run, so a run whose awaits outlive an account switch can
  // recognise that it has been superseded and drop its answer on the floor.
  const generation = useRef(0);

  useEffect(() => {
    const gen = ++generation.current;

    // 'loading' is not "signed out". Settling closed here and reopening a
    // moment later is the 404 flash this hook exists to avoid.
    if (isLoading) {
      setAccess(PENDING);
      return;
    }
    if (!customerId) {
      setAccess(IDLE);
      return;
    }

    setAccess(PENDING);
    void (async () => {
      // Both awaits are fail-closed on their own: the token is null when
      // there is no email or no WebCrypto, and the fetch resolves to the
      // closed value rather than rejecting.
      const [token, values] = await Promise.all([
        mumHubAudienceToken(email),
        mumHubRemoteConfigOnce(),
      ]);
      if (generation.current !== gen) return;
      setAccess({
        status: "settled",
        allowed: decideMumHubAccess({
          globalEnabled: values.globalEnabled,
          allowlist: values.allowlist,
          token,
        }),
      });
    })();
  }, [isLoading, customerId, email]);

  return access;
}
